import React, { useCallback, useState } from 'react'

import Image from 'next/image'

import { PaddingProps } from 'styled-system'

import { ReviewWidget, SectionWrapper, VideoPlayer } from 'Components/Blocks'
import ClutchBadges from 'Components/Sections/WeLiveUpToOurReputation/ClutchBadges'
import SuccessBadge from 'Components/Sections/WeLiveUpToOurReputation/SuccessBadge'
import { Column, Row, Text } from 'Components/UI'

import {
  SuccessClutchImage,
  SuccessUpworkImage,
  VideoCoverImage,
} from 'Assets/Image'
import { ColumnsGlyph, PlayGlyph } from 'Assets/Svg/Icons'

import { SOCIAL_LINKS } from 'Constants/ExternalLinks'

import {
  Content,
  CoverHolder,
  DesktopOnly,
  MobileOnly,
  PauseButton,
  PlayButton,
  TabletOnly,
  WidgetRow,
} from './styles'

const SUCCESS_CLUTCH_IMAGE_ALT =
  "Badge with a star icon and text reading 'Clients say we deliver on Clutch'"

const SUCCESS_UPWORK_IMAGE_ALT =
  "Badge with a crown icon and text reading '100% Job Success on Upwork'"

type Props = PaddingProps & {
  videoFullUrl?: string
}

// The badge variants all render (SSR included) and CSS media queries pick
// the visible set — the old isMobile/isTablet/isDesktop gates were all
// false at SSR, so crawlers never saw the Clutch/Upwork badges.
// Measured once the player has laid out (the holder is sized by the video, so
// it reads 300x150 before metadata arrives): 1440px -> 880 CSS px, 412 -> 380.
const VIDEO_COVER_SIZES =
  '(min-width: 1520px) 880px, (min-width: 768px) 62vw, 100vw'

function WeLiveUpToOurReputationSection({ videoFullUrl, ...rest }: Props) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [wasVideoStarted, setWasVideoStarted] = useState(false)

  const playVideo = useCallback(() => {
    setWasVideoStarted(true)
    setIsVideoPlaying(true)
  }, [])

  return (
    <SectionWrapper {...rest} asCard>
      <Column alignCenter gap={17}>
        <Text as="h2" center medium title title40>
          We live up to our reputation
        </Text>

        <Row alignCenter gap={19} px={[0, 0, 0, 0, 11]}>
          <DesktopOnly>
            <SuccessBadge
              alt={SUCCESS_CLUTCH_IMAGE_ALT}
              href={SOCIAL_LINKS.CLUTCH}
              image={SuccessClutchImage}
            />
          </DesktopOnly>

          <Content>
            <CoverHolder
              animate={{ opacity: isVideoPlaying || wasVideoStarted ? 0 : 1 }}
              initial={{ opacity: 1 }}
            >
              <Image
                alt="Mathieu Diab, CEO of WorkAxle in Montreal, sitting indoors with a relaxed posture, wearing a black T-shirt with humorous engineer definition text, with a blurred office background"
                fill
                sizes={VIDEO_COVER_SIZES}
                src={VideoCoverImage}
              />
            </CoverHolder>

            <VideoPlayer
              playing={isVideoPlaying}
              src={videoFullUrl}
              onClick={() => setIsVideoPlaying(false)}
            />

            <PlayButton
              animate={{ opacity: isVideoPlaying ? 0 : 1 }}
              aria-label="Play the video"
              initial={{ opacity: 1 }}
              style={{
                cursor: isVideoPlaying ? 'default' : 'pointer',
              }}
              onClick={playVideo}
            >
              {PlayGlyph()}
            </PlayButton>

            <PauseButton
              animate={{ opacity: isVideoPlaying ? 1 : 0 }}
              aria-label="Pause the video"
              initial={{ opacity: 0 }}
              style={{
                cursor: !isVideoPlaying ? 'default' : 'pointer',
              }}
              onClick={() => setIsVideoPlaying(prevState => !prevState)}
            >
              {ColumnsGlyph()}
            </PauseButton>
          </Content>

          <DesktopOnly>
            <SuccessBadge
              alt={SUCCESS_UPWORK_IMAGE_ALT}
              href={SOCIAL_LINKS.UPWORK}
              image={SuccessUpworkImage}
            />
          </DesktopOnly>
        </Row>

        <Column alignCenter fullWidth gap={7}>
          <MobileOnly>
            <Row gap={7}>
              <SuccessBadge
                alt={SUCCESS_CLUTCH_IMAGE_ALT}
                href={SOCIAL_LINKS.CLUTCH}
                image={SuccessClutchImage}
              />

              <SuccessBadge
                alt={SUCCESS_UPWORK_IMAGE_ALT}
                href={SOCIAL_LINKS.UPWORK}
                image={SuccessUpworkImage}
              />
            </Row>
          </MobileOnly>

          <WidgetRow>
            <TabletOnly>
              <SuccessBadge
                alt={SUCCESS_CLUTCH_IMAGE_ALT}
                href={SOCIAL_LINKS.CLUTCH}
                image={SuccessClutchImage}
              />
            </TabletOnly>

            <ReviewWidget />

            <TabletOnly>
              <SuccessBadge
                alt={SUCCESS_UPWORK_IMAGE_ALT}
                href={SOCIAL_LINKS.UPWORK}
                image={SuccessUpworkImage}
              />
            </TabletOnly>
          </WidgetRow>
        </Column>

        <ClutchBadges />
      </Column>
    </SectionWrapper>
  )
}

export default WeLiveUpToOurReputationSection
